import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PAIN_SEVERITY_COLOURS,
  REST_FALLBACK_SEC,
  exercisePainSeverity,
  resolveAthleteEnergy,
  type PlanStarter,
  type WorkoutEntryGroup,
} from '@heraion/shared';

import { useExerciseNameBySlug } from './exerciseName';
import { useRestTimer } from './useRestTimer';
import { useHoldTimer } from './useHoldTimer';
import { useStopAllTimers } from './useStopAllTimers';
import { useTheme } from './theme';
import { useLoggerCatalog } from './useLoggerCatalog';
import { usePlateCalc } from './usePlateCalc';
import { useLoggerDraft } from './state/loggerDraft';
import { makeStyles } from './loggerStyles';
import { workoutTotals } from './totalsMath';
import { suggestWorkoutName } from './workoutNameSuggest';
import { useRestPicker } from './useRestPicker';
import { useTimerDefaults } from './useTimerDefaults';
import { useDraftPersistence } from './useDraftPersistence';
import { useSaveWorkout } from './useSaveWorkout';
import { useWorkoutEnergyReadout } from './useWorkoutEnergyReadout';
import { usePostSaveSummary } from './usePostSaveSummary';
import { usePrRecordsResource } from './usePrRecordsResource';
import { useSetCyclePill } from './useSetCyclePill';
import { buildGroupCatalog, buildUnits, groupDisplayLabel } from './loggerUnits';
import { useGroupManagement } from './useGroupManagement';
import { useCopyHistory } from './useCopyHistory';
import { useAddExercise } from './useAddExercise';
import { useLiveAnnouncement } from '../primitives/useLiveAnnouncement';
import { useDiscardWorkout } from './useDiscardWorkout';
import { deriveLoggerMode } from './loggerMode';
import type { EditingTarget, LoggerOnSaved } from './LoggerScreen';
import type { LoggerHostDeps } from './hostDeps';

type UseLoggerScreenArgs = {
  visible: boolean;
  onClose: () => void;
  editing: EditingTarget | null;
  planStarter: PlanStarter | null;
  onSaved: LoggerOnSaved;
  deps: LoggerHostDeps;
};

// Composite orchestration hook for LoggerScreen (Track R). Holds the
// entire screen state machine — draft reducer, the 8 composite logger
// hooks, async catalog/records init, the 5 effects, and the memoized
// derivations (units / groupCatalog / totals / name+pain
// resolvers) — and returns a flat bag the orchestration shell renders.
// Extracted VERBATIM out of the LoggerScreen function body: hook-call
// order and effect order are preserved exactly (rules-of-hooks + the
// draftPersistence→restReset→catalog→records→prStamp→save sequencing).
export function useLoggerScreen({
  visible,
  onClose,
  editing,
  planStarter,
  onSaved,
  deps,
}: UseLoggerScreenArgs) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { profile, refreshProfile } = deps;
  const defaultUnit = profile.unitsPreference;
  const effortScale = profile.effortScale;
  // The mode contract (PE-22 slice A): ONE derivation, read by everything
  // downstream instead of the six independent re-derivations of the same bit
  // this replaced. Declared here, above its first consumer — it is a pure
  // function, not a hook, so the hook-call order below is untouched.
  // Surfaced in the context so the timeline logger can bind a completed set
  // for editing (no pending set on an all-logged saved workout — slice 5 /
  // item 1, 2026-05-31).
  const mode = deriveLoggerMode(editing);
  const [draft, dispatch, workoutState] = useLoggerDraft();
  // Post-save summary + share (own hook, 500-line cap): what the body shows
  // after a 'sent' save instead of closing, plus the imperative handle the
  // post-save Share button reaches the card through. See usePostSaveSummary.
  const {
    postSaveSummary,
    setPostSaveSummary,
    sessionResult,
    summaryCardRef,
    postSaveSharing,
    sharePostSave,
  } = usePostSaveSummary(deps.apiFetch);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** When set, the swap picker is open for that entry index. The
   *  entry's exerciseId becomes `current` in the picker so suggestions
   *  rank against the right slug. */
  const [swapEntryIdx, setSwapEntryIdx] = useState<number | null>(null);
  // Effort scale-switch window (D2-S5). One hoisted instance (like the plate
  // calc) opened from any set row's effort-panel gear — it edits the global
  // Profile.effortScale, not a per-set value, so a per-row modal would be
  // wrong (and wasteful).
  const [effortScaleOpen, setEffortScaleOpen] = useState(false);
  // Entry-tip popup (1.70.0). The `?` pill on each entry opens a Modal
  // with description + dual worked-muscles bodymap + ranked
  // alternatives. Only one tip can be open at a time so the state is a
  // single index (or null) rather than a Set. Replaces the 1.67.5
  // inline-description toggle. Design picks per
  // feedback_quick_tip_pill_layout.
  const [tipEntryIdx, setTipEntryIdx] = useState<number | null>(null);
  /** Plate calculator modal state. `targetKg` seeds the modal's input
   *  with the latest filled set's weight (canonical kg) on the entry
   *  the user tapped Plates on; subsequent edits to the modal input
   *  don't write back to the row — the calc is read-only. */
  const [catalog, setCatalog] = useLoggerCatalog(visible, deps.catalog);
  // FK resolver for DraftEntry.exerciseName / m.entry.exerciseName.
  // Per [[feedback_no_denormalized_display_snapshots]] we never read
  // the in-flight snapshot for display — always resolve from the live
  // catalog with the snapshot as deleted-row fallback only.
  const getExerciseNameBySlug = useExerciseNameBySlug(catalog);

  // Pain-risk colour for an entry's exercise. Looks up the catalog row,
  // combines its primary/secondary load with the user's painMap, returns
  // the severity colour or undefined when the exercise is safe (or the
  // catalog hasn't loaded yet). Same logic the Catalog dot uses on the
  // pickers — applied here so the Logger title itself flags risk
  // without the user having to tap the `?` pill to see the banner.
  const painColorFor = useCallback(
    (slug: string): string | undefined => {
      if (!catalog) return undefined;
      const ex = catalog.find((c) => c.slug === slug);
      if (!ex) return undefined;
      const risk = exercisePainSeverity(ex, profile.painMap);
      return risk ? PAIN_SEVERITY_COLOURS[risk.severity] : undefined;
    },
    [catalog, profile.painMap],
  );

  const totals = useMemo(
    () => workoutTotals(draft, catalog, profile.bodyweightKg ?? 0),
    [draft, catalog, profile.bodyweightKg],
  );
  // HC-8 — the workout energy readout for the footer + post-save card (own hook,
  // 500-line cap): stored measured value on an enriched-session edit, else the
  // live estimate. See useWorkoutEnergyReadout.
  const energyReadout = useWorkoutEnergyReadout(draft, effortScale, profile, editing, mode);
  // Live auto-name suggestion: shown as the title default + the save-time
  // fallback when the user leaves the name blank. "<what was trained> - <date>"
  // from the loaded sets ([[workoutNameSuggest]]).
  const nameSuggestion = useMemo(
    () =>
      suggestWorkoutName({
        draft, catalog, effortScale, t,
        locale: i18n.resolvedLanguage ?? 'en',
      }),
    [draft, catalog, effortScale, i18n.resolvedLanguage, t],
  );
  const restTimer = useRestTimer(deps.timerNotification, deps.playTimerCue);

  // The PR comparators + the late-arrival repair effect (own hook, 500-line
  // cap): /api/me/records for the click-time PR check, the stored MAXes for
  // the regression ⬇️ marker. See usePrRecordsResource.
  const { prHistory, refreshRecords, maxMeta, maxesLoaded } =
    usePrRecordsResource({ visible, draft, dispatch, deps });

  // A live bodyweight set done with no Profile.bodyweightKg: stamp
  // bodyweightNeededAt ONCE. The server derives the bell row from it (#296);
  // nothing is shown in the session. The ref covers the gap before the
  // profile re-reads; a failed write releases it so the next set retries.
  const bwNeededMarkedRef = useRef(false);
  const markBodyweightNeeded = useCallback(() => {
    // Hydrating: the defaults are not the athlete's data (a weight may exist).
    if (deps.profileLoaded === false) return;
    if (profile.bodyweightKg || profile.bodyweightNeededAt) return;
    if (bwNeededMarkedRef.current) return;
    bwNeededMarkedRef.current = true;
    deps
      .patchProfile({ bodyweightNeededAt: new Date().toISOString() })
      .then(() => deps.refreshProfile())
      .catch(() => {
        bwNeededMarkedRef.current = false;
      });
  }, [profile.bodyweightKg, profile.bodyweightNeededAt, deps]);

  const cycleSetPill = useSetCyclePill({
    entries: draft.entries,
    dispatch,
    prHistory,
    // Bidirectional MAX ⬇️ marker (next/measurement-contract.md «Il MAX dinamico: il come»). Pass the
    // {valueKg, setAt} map only once loaded, so the regression check never
    // fires against an empty map (mirrors the prHistory null-guard).
    currentMaxes: maxesLoaded ? maxMeta : null,
    catalog,
    defaultRestSec: profile.defaultRestSec,
    restTimer,
    startedAt: draft.startedAt,
    // Drives the clock (in-window placement on 'log-past' instead of the
    // live stamp) AND the live cues (rest countdown, bodyweight marker:
    // 'log' only).
    mode,
    onBodyweightSetCompleted: markBodyweightNeeded,
  });

  // Hold/cardio timer for duration sets. It owns the COUNT only: the seconds
  // and the completion belong to the effort meter now, so no dispatch and no
  // cycleSetPill here (MX-5 slice 3 — one completion path for every set kind).
  const hold = useHoldTimer({
    entries: draft.entries,
    catalog,
    profileDefaultDurationSec: profile.defaultDurationSec,
    timerNotification: deps.timerNotification,
    playTimerCue: deps.playTimerCue,
  });
  // Every timer stops on a new session, a save or a discard (useStopAllTimers).
  const stopAllTimers = useStopAllTimers({
    restTimer, hold, timerNotification: deps.timerNotification, sessionKey: draft.startedAt,
  });

  // Quick-edit Exercise.defaultRestSec from the rest banner without
  // leaving the workout. Antenore's 2026-05-20 complaint: the catalog
  // edit-default-rest path lived only on web and was buried behind
  // Settings → Catalog → exercise → edit. The banner button below
  // surfaces the same control mid-workout: tap → Alert preset list →
  // PATCH /api/exercises/:slug. Updates the in-memory catalog so the
  // next set on this exercise picks up the new default without a
  // full page reload; the catalog source is marked stale so the next
  // read revalidates (#342).
  // Rest-default picker (1.82.0 slider Modal). State + the PATCH
  // side-effects live in useRestPicker; the screen wires the returned
  // handlers into the rest banner button + RestDefaultPicker. See
  // Track R.
  const restPicker = useRestPicker({
    catalog,
    setCatalog,
    profileDefaultRestSec: profile.defaultRestSec,
    refreshProfile,
    activeRestExerciseId: restTimer.state.exerciseId,
    t,
    durableWrite: deps.durableWrite,
    patchProfile: deps.patchProfile,
    notify: deps.notify,
    onCatalogSaved: () => deps.catalog.invalidate(),
  });
  // In-Logger per-exercise timer-defaults sheet (the ⚙ on the start-timer
  // affordance) — sets rest + countdown/hold default for the active exercise
  // mid-workout. Shares the PATCH with the Settings card via
  // saveExerciseTimerDefaults; LoggerModals mounts the ExerciseTimerSheet.
  const timerDefaults = useTimerDefaults({
    catalog,
    setCatalog,
    profileDefaultRestSec: profile.defaultRestSec,
    profileDefaultDurationSec: profile.defaultDurationSec,
    t,
    durableWrite: deps.durableWrite,
    notify: deps.notify,
    onSaved: () => deps.catalog.invalidate(),
  });
  // Manual rest start (inbox 0b1a7474): the header clock arms a rest on demand
  // with the profile default. ⭐ TM-19 — it BINDS that rest to the exercise the
  // caller names, undoing the earlier "unbound on purpose": the tag is what
  // FocusPanelRest reads to draw the ⚙, and what the picker needs to know which
  // catalog row to PATCH, so an untagged rest silently removed both. The slug
  // comes from the cursor, which lives above this hook (LoggerScreen resolves
  // it) — same reason useRestAdvance is called up there.
  const startManualRest = useCallback(
    (exerciseId?: string) => {
      restTimer.start(profile.defaultRestSec ?? REST_FALLBACK_SEC, exerciseId);
    },
    [restTimer, profile.defaultRestSec],
  );
  const onCopyHistory = useCopyHistory({
    entries: draft.entries,
    defaultUnit,
    dispatch,
    confirm: deps.confirm,
    t,
  });

  const [groupPickerEntryIdx, setGroupPickerEntryIdx] = useState<number | null>(
    null,
  );
  // Superset ☰-menu + add-to-group UI state (inbox 963ca6f2 batch 2).
  // Spread into the return — the body doesn't read it (LoggerModals consumes it).
  const groupMgmt = useGroupManagement();
  /** Validation / build error — translatable key OR raw text. */
  const [error, setError] = useState<string | null>(null);
  /** Server reply when the API rejected the save — kept verbatim so
   *  the user can see the actual reason ("invalid_payload (...)",
   *  "duplicate_id", …). */
  const [serverError, setServerError] = useState<string | null>(null);

  // Draft hydration + AsyncStorage mirroring (incl. the Resume/Discard
  // cold-boot prompt) live in useDraftPersistence. It returns the two
  // refs the rest of the screen still touches: persistedHydratedRef
  // (read/reset by save + cancel/discard) and draftRef (post-dispatch
  // lookups + background flush).
  const { persistedHydratedRef, draftRef, restoredKeyRef } = useDraftPersistence({
    visible,
    editing,
    mode,
    planStarter,
    catalog,
    apiFetch: deps.apiFetch,
    // EQ-1-PROGSTART-SILENT: so a plan-started entry gets the add-time machine.
    locationUnavailable: profile.locationUnavailable,
    equipmentDefault: profile.equipmentDefault,
    defaultUnit,
    draft,
    workoutState,
    // ⭐ LG-53 — already in scope from usePostSaveSummary above; no new state.
    postSaved: postSaveSummary !== null,
    dispatch,
    t,
    setError,
    setServerError,
    confirm: deps.confirm,
  });

  const usedSlugs = useMemo(
    () => draft.entries.map((e) => e.exerciseId),
    [draft.entries],
  );

  // Group catalog + superset-fusion units (pure builders in loggerUnits).
  const groupCatalog = useMemo(
    () => buildGroupCatalog(draft.entries),
    [draft.entries],
  );

  const groupLabelFor = (group: WorkoutEntryGroup | null): string =>
    groupDisplayLabel(group, groupCatalog, t);

  const units = useMemo(() => buildUnits(draft.entries), [draft.entries]);

  const styles = makeStyles(theme);

  /** Pick the most recent set on this entry that has a usable weight,
   *  convert to canonical kg, and open the plate calculator seeded
   *  with that value. Inbox 8c6a9371 (1.66.5): seed from the set the
   *  user is editing, not the last non-zero set in the entry, so the
   *  calc reflects what the lifter sees on screen. Apply then writes
   *  back to that same set instead of guessing the next pending one. */
  // Plate calculator (state + seed/apply orchestration in usePlateCalc;
  // pure seed/format/target math in plateCalcMath). See Track R.
  const plate = usePlateCalc({
    entries: draft.entries,
    catalog,
    dispatch,
  });

  // #56: the history prefill changes sets with no visible cue; say it.
  const { speak, region: liveRegion } = useLiveAnnouncement();
  const { onPick, addMany, createSuperset } = useAddExercise({
    dispatch,
    defaultUnit,
    setPickerOpen,
    draftRef,
    catalog,
    // EQ-1-GYM-COLDSTART R6 — so the add path's silent machine apply cannot
    // attach one the athlete declared is not at this place.
    ...(profile.locationUnavailable
      ? { locationUnavailable: profile.locationUnavailable }
      : {}),
    // EQ-1-PIN-DEFAULT — the add path resolves silently and never asks, so an
    // election is the only way the athlete's choice can reach it.
    ...(profile.equipmentDefault
      ? { equipmentDefault: profile.equipmentDefault }
      : {}),
    // LG-71 — so an exercise the 50-session window is too short to see still
    // opens with the weight and the set count of the last time it was done.
    apiFetch: deps.apiFetch,
    onHistorySeeded: () =>
      speak(
        t('mobile.logger.setsCarriedOverA11y', {
          defaultValue: 'Sets carried over from last time.',
        }),
      ),
  });

  const { submitting, onSave, onSaveAsNew } = useSaveWorkout({
    draft,
    editing,
    mode,
    catalog,
    prHistory,
    defaultUnit,
    effortScale,
    athlete: resolveAthleteEnergy(profile),
    resolvedLanguage: i18n.resolvedLanguage,
    nameSuggestion,
    dispatch,
    persistedHydratedRef,
    onSaved,
    onClose,
    refreshRecords,
    stopAllTimers,
    setError,
    setServerError,
    setPostSaveSummary,
    t,
    deps,
  });

  const onCancel = useDiscardWorkout({
    submitting,
    draft,
    mode,
    editing,
    effortScale,
    dispatch,
    persistedHydratedRef,
    onClose,
    confirm: deps.confirm,
    t,
    stopAllTimers,
  });

  return {
    theme,
    t,
    i18n,
    // MX-10 — the timeline card's «best» reads the all-time PR records, the
    // only source with no window in it. Already fetched here for the PR pill.
    prHistory,
    profile,
    deps,
    styles,
    draft,
    dispatch,
    defaultUnit,
    energyReadout,
    postSaveSummary,
    sessionResult,
    summaryCardRef,
    postSaveSharing,
    sharePostSave,
    pickerOpen,
    setPickerOpen,
    swapEntryIdx,
    setSwapEntryIdx,
    effortScaleOpen,
    setEffortScaleOpen,
    tipEntryIdx,
    setTipEntryIdx,
    catalog,
    getExerciseNameBySlug,
    painColorFor,
    restTimer,
    hold,
    startManualRest,
    cycleSetPill,
    restPicker,
    timerDefaults,
    onCopyHistory,
    groupPickerEntryIdx,
    setGroupPickerEntryIdx,
    ...groupMgmt,
    error,
    serverError,
    usedSlugs,
    groupCatalog,
    groupLabelFor,
    units,
    plate,
    liveRegion,
    restoredKeyRef,
    onPick,
    addMany,
    createSuperset,
    submitting,
    onSave,
    onSaveAsNew,
    onCancel,
    totals,
    nameSuggestion,
    mode,
  };
}
